import type { LegalDocument } from "./privacy-content";

export const DEALER_CONDUCT_CONTENT: LegalDocument = {
  title: "Dealer Code of Conduct",
  lastUpdated: "February 2026",
  intro:
    "Dealers participating in Motosport Software services are expected to conduct business lawfully, transparently, and professionally. These standards are intended to protect Buyers, Sellers, other Dealers, Transporters, and the integrity of the platform.",
  sections: [
    {
      title: "1. Legal and Professional Compliance",
      paragraphs: [
        "Dealers must maintain all licenses, registrations, bonds, insurance, and other qualifications required for their operations and must comply with applicable federal, state, and local laws and regulations.",
        "Dealers are responsible for the conduct of their owners, employees, agents, contractors, and representatives when using Motosport Software services.",
      ],
    },
    {
      title: "2. Accurate Vehicle Information",
      paragraphs: [
        "Vehicle descriptions, prices, mileage, condition, equipment, availability, title status, history, photographs, and other material information must be accurate to the dealer's knowledge and updated promptly when circumstances change.",
        "Dealers must not conceal known material defects, title issues, prior damage, branded-title status, odometer concerns, or other facts that could reasonably affect a participant's decision.",
      ],
    },
    {
      title: "3. Transparent Pricing and Fees",
      paragraphs: [
        "Dealers must clearly disclose the vehicle price and all mandatory dealer-imposed charges before asking a Buyer or other participant to commit to a transaction.",
        "Advertised or submitted pricing must not be misleading, conditioned on undisclosed qualifications, or increased through unexpected add-ons, products, or fees. Government charges, taxes, registration costs, and legitimately optional products should be identified separately.",
      ],
    },
    {
      title: "4. Relevant and Good-Faith Responses",
      paragraphs: [
        "Dealers should respond to requests only when they have a vehicle or opportunity that reasonably fits the stated requirements, or when they clearly identify any meaningful differences for the recipient to consider.",
        "Dealers must not use irrelevant, repetitive, deceptive, or automated responses to obtain contact information, generate artificial activity, or divert participants from their stated needs.",
      ],
    },
    {
      title: "5. Respectful, Buyer-Controlled Communication",
      paragraphs: [
        "Dealers must communicate professionally and respect a participant's decision regarding when and how to connect. Harassment, intimidation, discriminatory conduct, excessive contact, deceptive urgency, and high-pressure tactics are prohibited.",
        "A participant's decision not to proceed must be respected. Contact preferences and applicable communication-consent requirements must be followed.",
      ],
    },
    {
      title: "6. Privacy and Appropriate Data Use",
      paragraphs: [
        "Information obtained through Motosport Software may be used only for legitimate platform-related business purposes and in accordance with applicable privacy laws, the Privacy Policy, the Terms of Use, and any separate agreement.",
        "Dealers must not sell, improperly share, scrape, harvest, or use participant information for unrelated solicitation, profiling, or other unauthorized purposes.",
      ],
    },
    {
      title: "7. Fair Dealing with Other Participants",
      paragraphs: [
        "Dealers must act honestly in retail, wholesale, appraisal, auction, transportation, and other platform-supported activities. Offers, bids, commitments, vehicle availability, payment terms, and delivery expectations must be made in good faith.",
        "Dealers must not manipulate platform activity, submit false requests or offers, impersonate another party, interfere with another participant's transaction, or misrepresent authority to act for a person or organization.",
      ],
    },
    {
      title: "8. Transaction Documentation and Cooperation",
      paragraphs: [
        "Dealers should maintain appropriate records supporting vehicle representations, pricing, disclosures, communications, and transaction terms. Dealers should also provide reasonable cooperation when Motosport Software reviews a complaint, safety concern, suspected violation, or platform dispute.",
        "Dealers remain responsible for completing all legally required disclosures and transaction documents outside the platform where applicable.",
      ],
    },
    {
      title: "9. Platform Integrity and Security",
      paragraphs: [
        "Dealers must protect account credentials, use the services only for authorized purposes, and promptly report suspected unauthorized access, fraud, security incidents, or misuse.",
        "Attempts to bypass platform safeguards, introduce malicious code, access restricted information, disrupt services, or exploit another participant are prohibited.",
      ],
    },
    {
      title: "10. Reporting and Enforcement",
      paragraphs: [
        "Motosport Software may review reported or suspected violations and may request information reasonably necessary to evaluate the matter. Depending on the circumstances, corrective action may include education, warnings, content removal, feature restrictions, suspension, or termination of access.",
        "Serious or repeated misconduct may be reported to affected participants, service providers, regulators, law enforcement authorities, or other appropriate parties when permitted or required by law.",
      ],
    },
    {
      title: "11. Changes and Questions",
      paragraphs: [
        "Motosport Software may update this Dealer Code of Conduct as its services and legal obligations evolve. Continued use after an updated version becomes effective constitutes acceptance to the extent permitted by law.",
        "Questions about these standards or reports of potential violations may be submitted through the Contact form available on the Motosport Software website.",
      ],
    },
  ],
};
